import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';
import { AlertTriangle, X } from '../../icons';
import { getSharedLang } from '../../lib/base-i18n';
import './LayerPopup.css';

/**
 * 공통 레이어팝업 — 브라우저 window.confirm / window.alert 를 대체하는 모달.
 *
 * 사용(기존 호출부 소폭 교체만, 동작 동등):
 *   - confirm: `if (!(await layerConfirm(msg))) return;`   (Promise<boolean>)
 *   - alert  : `void layerAlert(msg)` 또는 `await layerAlert(msg)`
 *
 * 루트에 <LayerPopupHost/> 1회 마운트(App.tsx). 호출은 컴포넌트 외부(모듈 함수)에서도 가능 —
 * imperative API 라 기존 핸들러 구조를 거의 그대로 유지(회귀 위험 최소화).
 * 동시 호출은 큐로 순차 표시. ESC=취소, Enter=확인.
 */

type Kind = 'confirm' | 'alert' | 'prompt' | 'choice';
type Variant = 'warn' | 'error' | 'info';

export interface LayerPopupOpts {
  title?: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
  variant?: Variant;
}

interface PopupItem extends LayerPopupOpts {
  id: number;
  kind: Kind;
  message: string;
  resolve: (v: boolean) => void;
  /** prompt 전용 (additive) — kind='prompt' 일 때만 사용. */
  promptResolve?: (v: string | null) => void;
  placeholder?: string;
  defaultValue?: string;
  /** choice 전용 (additive, 2026-10-14) — kind='choice' 일 때만 사용. */
  choiceResolve?: (v: LayerChoice) => void;
}

/**
 * ⭐ 2026-10-14 (additive) — 예/아니오/닫기 세 갈래. 예='yes' · 아니오='no' · 닫기(×)·ESC·배경=null.
 *   confirm 은 «아니오 = 닫기»라 «아니오지만 진행»을 못 가른다 — 그 자리용.
 */
export type LayerChoice = 'yes' | 'no' | null;

interface PopupStore {
  queue: PopupItem[];
  push: (item: PopupItem) => void;
  resolveTop: (v: boolean) => void;
  /** prompt 전용 — 확인=입력값, 취소·ESC·배경=null. */
  resolveTopPrompt: (text: string | null) => void;
  /** choice 전용 — 예='yes' · 아니오='no' · 닫기·ESC·배경=null. */
  resolveTopChoice: (v: LayerChoice) => void;
}

const usePopupStore = create<PopupStore>((set, get) => ({
  queue: [],
  push: (item) => set((s) => ({ queue: [...s.queue, item] })),
  resolveTop: (v) => {
    const top = get().queue[0];
    if (!top) return;
    top.resolve(v);
    set((s) => ({ queue: s.queue.slice(1) }));
  },
  resolveTopPrompt: (text) => {
    const top = get().queue[0];
    if (!top) return;
    top.promptResolve?.(text);
    set((s) => ({ queue: s.queue.slice(1) }));
  },
  resolveTopChoice: (v) => {
    const top = get().queue[0];
    if (!top) return;
    top.choiceResolve?.(v);
    set((s) => ({ queue: s.queue.slice(1) }));
  },
}));

let _seq = 1;
function show(kind: Kind, message: string, opts?: LayerPopupOpts): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    usePopupStore.getState().push({ id: _seq++, kind, message, resolve, ...opts });
  });
}

/** window.confirm 대체 — 확인 true / 취소·ESC·배경 false. */
export function layerConfirm(message: string, opts?: LayerPopupOpts): Promise<boolean> {
  return show('confirm', message, opts);
}
/** window.alert 대체 — 확인 시 resolve. */
export function layerAlert(message: string, opts?: LayerPopupOpts): Promise<void> {
  return show('alert', message, { variant: 'error', ...opts }).then(() => undefined);
}
/** window.prompt 대체 — 확인=입력값(string), 취소·ESC·배경=null. */
export function layerPrompt(
  message: string,
  opts?: LayerPopupOpts & { placeholder?: string; defaultValue?: string },
): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    usePopupStore.getState().push({
      id: _seq++,
      kind: 'prompt',
      message,
      resolve: () => {},
      promptResolve: resolve,
      variant: 'info',
      ...opts,
    });
  });
}

/**
 * 예/아니오/닫기 — 예='yes' · 아니오='no' · 닫기(×)·ESC·배경=null (additive, 2026-10-14).
 *   confirmText·cancelText 로 «예»·«아니오» 글자를 준다(없으면 확인·취소).
 */
export function layerChoice(message: string, opts?: LayerPopupOpts): Promise<LayerChoice> {
  return new Promise<LayerChoice>((resolve) => {
    usePopupStore.getState().push({
      id: _seq++,
      kind: 'choice',
      message,
      resolve: () => {},
      choiceResolve: resolve,
      ...opts,
    });
  });
}

const LABELS: Record<string, { ok: string; cancel: string; confirm: string; notice: string; close?: string }> = {
  ko: { ok: '확인', cancel: '취소', confirm: '확인이 필요합니다', notice: '알림', close: '닫기' },
  en: { ok: 'OK', cancel: 'Cancel', confirm: 'Please confirm', notice: 'Notice', close: 'Close' },
  ja: { ok: 'OK', cancel: 'キャンセル', confirm: '確認が必要です', notice: 'お知らせ', close: '閉じる' },
};
function labels() {
  return LABELS[getSharedLang()] ?? LABELS.en ?? { ok: 'OK', cancel: 'Cancel', confirm: 'Please confirm', notice: 'Notice', close: 'Close' };
}

export function LayerPopupHost() {
  const queue = usePopupStore((s) => s.queue);
  const resolveTop = usePopupStore((s) => s.resolveTop);
  const resolveTopPrompt = usePopupStore((s) => s.resolveTopPrompt);
  const resolveTopChoice = usePopupStore((s) => s.resolveTopChoice);
  const top = queue[0];
  const isPrompt = top?.kind === 'prompt';
  const isChoice = top?.kind === 'choice';

  // prompt 입력값 — 팝업(top.id) 전환 시 defaultValue 로 리셋.
  const [text, setText] = useState('');
  useEffect(() => { setText(top?.defaultValue ?? ''); }, [top?.id, top?.defaultValue]);

  /** 닫기 — ESC·배경 클릭이 같은 답을 준다(choice=null · prompt=null · confirm/alert=false). */
  const dismissTop = useCallback(() => {
    if (isChoice) resolveTopChoice(null);
    else if (isPrompt) resolveTopPrompt(null); else resolveTop(false);
  }, [isChoice, isPrompt, resolveTop, resolveTopPrompt, resolveTopChoice]);

  useEffect(() => {
    if (!top) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        dismissTop();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        // prompt 는 Host 의 최신 text 를 별도 핸들러에서 처리 — input onKeyDown 이 담당.
        if (isChoice) resolveTopChoice('yes');
        else if (!isPrompt) resolveTop(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [top, isPrompt, isChoice, resolveTop, resolveTopChoice, dismissTop]);

  if (!top) return null;

  const L = labels();
  const variant: Variant = top.variant ?? (top.danger ? 'error' : 'warn');
  const title = top.title ?? (top.kind === 'confirm' || isChoice ? L.confirm : L.notice);
  const confirmText = top.confirmText ?? L.ok;
  const cancelText = top.cancelText ?? L.cancel;

  return createPortal(
    <div
      className="layerpop__backdrop"
      onMouseDown={(e) => {
        if (e.target !== e.currentTarget) return;
        dismissTop();
      }}
    >
      <div
        className={`layerpop layerpop--${variant}${isPrompt ? ' layerpop--prompt' : ''}${isChoice ? ' layerpop--choice' : ''}`}
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
      >
        {isChoice && (
          <button
            type="button"
            className="layerpop__close"
            aria-label={L.close}
            onClick={() => resolveTopChoice(null)}
          >
            <X size={16} />
          </button>
        )}
        <div className="layerpop__icon"><AlertTriangle size={20} /></div>
        <div className="layerpop__body">
          <h3 className="layerpop__title">{title}</h3>
          <p className="layerpop__msg">{top.message}</p>
          {isPrompt && (
            <input
              className="layerpop__input"
              value={text}
              placeholder={top.placeholder}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); resolveTopPrompt(text); } }}
              autoFocus
              aria-label={title}
            />
          )}
        </div>
        <div className="layerpop__actions">
          {(top.kind === 'confirm' || isPrompt || isChoice) && (
            <button
              type="button"
              className="layerpop__btn layerpop__btn--ghost"
              onClick={() => { if (isChoice) resolveTopChoice('no'); else if (isPrompt) resolveTopPrompt(null); else resolveTop(false); }}
            >
              {cancelText}
            </button>
          )}
          <button
            type="button"
            className={`layerpop__btn layerpop__btn--primary${top.danger ? ' layerpop__btn--danger' : ''}`}
            onClick={() => { if (isChoice) resolveTopChoice('yes'); else if (isPrompt) resolveTopPrompt(text); else resolveTop(true); }}
            autoFocus={!isPrompt}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
